import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";import { motion, AnimatePresence } from "framer-motion";
import {
  Camera, Upload, Store, User, Check, ArrowRight, MessageCircle, Download, Share2, X, Copy, LogIn, UserPlus, Calendar, Users, Shield, Settings, Award, ChevronDown, Telescope, Handshake,
} from "lucide-react";
import { VerifiedField } from "@/components/VerifiedField";
import { Panel, Eyebrow, MonoLabel, LiveDot } from "@/components/Panel";
import { RealQR } from "@/components/RealQR";
import { CHAPTERS, MEMBERS } from "@/data/seed";
import { usePublicEvent, publicApi, apiError, guardrail, loadRazorpay, pollUntilPaid, inr, EVENT_SLUG } from "@/lib/publicApi";
import { track } from "@/lib/track";
import { toast, Toaster } from "sonner";
import { useDemo } from "@/store/DemoStore";
import axios from "axios";
import { otpApi, staffLogin, MEMBER_TOKEN_KEY, ADMIN_TOKEN_KEY, apiErr } from "@/lib/authApi";
import { TOKEN_KEY as DEVADMIN_TOKEN_KEY } from "@/lib/devadminApi";
import { LoginModal } from "@/components/LoginModal";

// Simple registration modes: Member (full form) and Stall+Sponsor (contact-only)
const REG_MODES = ["member", "contact"];

// i18n copy for the hero + marketing nav
const COPY = {
  en: {
    navBrandH: "Members Day",
    navBrandS: "Davangere Region · 2026",
    navAgenda: "Agenda",
    navConclave: "One-to-One Conclave",
    navAbout: "About",
    navWhy: "Why Attend",
    navTravel: "Travel & Stay",
    navFaq: "FAQ",
    navRegister: "Register",
    eyebrow: "Davangere Region · First Edition",
    titleLine1: "Members Day",
    titleAccent: "Davangere",
    titleRest: "Region — 2026",
    slug: "Where Connections Become Opportunities.",
    slugParts: [["Where", "w"], ["Connections", "r"], ["Become", "w"], ["Opportunities.", "r"]],
    lede: "For the first time ever, BNI Davangere Region hosts Members Day — bringing BNI members from across regions together to connect, collaborate and celebrate.",
    pDate: "Date", pVenue: "Venue", pReach: "Reach",
    vDate: "Sat, 28 November 2026",
    vVenue: "Trishul Kalaabhavana, Davangere",
    vReach: "Members · Guests · Intl. Delegates",
    ctaRegister: "Register now",
    ctaAgenda: "View full agenda",
    ctaShare: "Save WhatsApp card",
    cdDays: "Days", cdHrs: "Hrs", cdMin: "Min", cdSec: "Sec",
    seatsLeft: "Seats left",
  },
  kn: {
    navBrandH: "ಮೆಂಬರ್ಸ್ ಡೇ",
    navBrandS: "ದಾವಣಗೆರೆ ರೀಜನ್ · 2026",
    navAgenda: "ಕಾರ್ಯಸೂಚಿ",
    navConclave: "ಒಂದು-ಒಂದು ಸಮಾವೇಶ",
    navAbout: "ಕಾರ್ಯಕ್ರಮದ ಬಗ್ಗೆ",
    navWhy: "ಏಕೆ ಭಾಗವಹಿಸಬೇಕು",
    navTravel: "ಪ್ರಯಾಣ ಮತ್ತು ವಸತಿ",
    navFaq: "ಪ್ರಶ್ನೆಗಳು",
    navRegister: "ನೋಂದಣಿ",
    eyebrow: "ದಾವಣಗೆರೆ ರೀಜನ್ · ಪ್ರಥಮ ಆವೃತ್ತಿ",
    titleLine1: "ಮೆಂಬರ್ಸ್ ಡೇ",
    titleAccent: "ದಾವಣಗೆರೆ",
    titleRest: "ರೀಜನ್ — 2026",
    slug: "ಸಂಪರ್ಕಗಳು ಅವಕಾಶಗಳಾಗುವಲ್ಲಿ.",
    slugParts: [["ಸಂಪರ್ಕಗಳು", "r"], ["ಅವಕಾಶಗಳಾಗುವಲ್ಲಿ.", "w"]],
    lede: "ಇತಿಹಾಸದಲ್ಲಿ ಮೊದಲ ಬಾರಿಗೆ, BNI ದಾವಣಗೆರೆ ರೀಜನ್ ಆಯೋಜಿಸುತ್ತಿದೆ ಮೆಂಬರ್ಸ್ ಡೇ — ವಿವಿಧ ರೀಜನ್‌ಗಳ BNI ಸದಸ್ಯರನ್ನು ಸಂಪರ್ಕ, ಸಹಯೋಗ ಮತ್ತು ಆಚರಣೆಗಾಗಿ ಒಂದೇ ಕಡೆ ತರುತ್ತಿದೆ.",
    pDate: "ದಿನಾಂಕ", pVenue: "ಸ್ಥಳ", pReach: "ವ್ಯಾಪ್ತಿ",
    vDate: "ಶನಿವಾರ, 28 ನವೆಂಬರ್ 2026",
    vVenue: "ತ್ರಿಶೂಲ ಕಲಾಭವನ, ದಾವಣಗೆರೆ",
    vReach: "ಸದಸ್ಯರು · ಅತಿಥಿಗಳು · ಅಂತಾರಾಷ್ಟ್ರೀಯ ಪ್ರತಿನಿಧಿಗಳು",
    ctaRegister: "ಈಗಲೇ ನೋಂದಾಯಿಸಿ",
    ctaAgenda: "ಪೂರ್ಣ ಕಾರ್ಯಸೂಚಿ",
    ctaShare: "WhatsApp ಕಾರ್ಡ್ ಉಳಿಸಿ",
    cdDays: "ದಿನ", cdHrs: "ಗಂ", cdMin: "ನಿ", cdSec: "ಸೆ",
    seatsLeft: "ಸೀಟ್‌ಗಳು ಬಾಕಿ",
  },
};

const REG_TARGET = 2000;
const REG_PAID   = 1841; // simulated for demo — 92% cap

const SPONSORS = [
  { name: "Shivex India Private Limited",      tier: "Title Sponsor",       brand: "SHIVEX" },
  { name: "Aakar GPR Works",                   tier: "Powered By Sponsor",  brand: "AAKAR GPR" },
  { name: "Veon · Vastu · Energy · Numerology", tier: "Powered By Sponsor", brand: "VEON" },
  { name: "BCL Creative AD Agency",            tier: "Branding Partner",    brand: "BCL" },
  { name: "Zerolabs Digitals",                 tier: "Technology Partner",  brand: "ZEROLABS DIGITALS" },
];

const AGENDA = [
  { time: "08:00 – 08:25", h: "Open Networking & High Tea",         p: "Kick off the day with open networking over a high tea — meet visiting members and set the tone.", tag: "Doors open" },
  { time: "08:30 – 09:00", h: "Opening Ceremony & Lighting",         p: "Traditional lamp lighting to formally inaugurate Members Day, led by the Davangere Region leadership." },
  { time: "09:00 – 09:15", h: "Welcome — Guests & Members",          p: "A short welcome address for guests and members setting the intent for the day." },
  { time: "09:25 – 09:40", h: "Stall Opening",                       p: "Ribbon-cutting for the partner and member stalls — take a first look before the sessions begin.", tag: "Stall floor" },
  { time: "09:45 – 09:55", h: "Sponsor Slot 1",                      p: "A dedicated slot for our sponsors to address the room." },
  { time: "10:00 – 10:20", h: "Power Pack Presentation — Guest",      p: "A high-impact Power Pack from a guest speaker to energise the room." },
  { time: "10:25 – 10:45", h: "Speaker 2 Presentation & Honouring",  p: "Featured speaker session followed by honouring of distinguished contributors." },
  { time: "11:00 – 13:00", h: "Power Pack 1-2-1 Session",            p: "Structured one-to-ones between local, visiting, and international members.", tag: "Book your slot" },
  { time: "13:00 – 13:30", h: "Stall Visit",                         p: "Walk the stall floor — meet sponsors, member businesses, and partner brands." },
  { time: "13:45 – 14:30", h: "Lunch Break",                         p: "A taste of Davangere — including the region's famous benne dosa — served alongside open networking." },
  { time: "15:00 – 15:30", h: "Guest Speaker Presentation — 3",      p: "The third featured guest speaker session of the day." },
  { time: "16:00 – 17:00", h: "Break",                               p: "Tea, coffee, and informal catch-ups before the evening block begins." },
  { time: "17:00 – 17:30", h: "Visitors Registration",               p: "Dedicated check-in window for evening visitors and invited guests." },
  { time: "18:00 – 18:30", h: "Visitor Orientation",                 p: "A short orientation for first-time visitors — what BNI is, and how the evening flows." },
  { time: "18:30 onward",  h: "Recognition + Power Pack by ED",      p: "Region-wide recognition followed by a Power Pack address from the Executive Director." },
  { time: "19:30 – 20:15", h: "Cultural Activities",                 p: "A showcase of local Davangere culture for outstation and international guests." },
  { time: "20:30 onward",  h: "Dinner · Session Closes by 21:30",    p: "Dinner and closing conversations. The programme wraps by 9:30 PM.", tag: "Wrap-up" },
];

const WEAVE = [
  { n: "01", h: "Tell us who you want to meet",   p: "At registration, pick your category and the regions, chapters, or industries you'd most like to connect with." },
  { n: "02", h: "Get your schedule automatically", p: "A few days before the event, you'll receive a personal one-to-one schedule — who, where, and when." },
  { n: "03", h: "Meet, connect, follow up",       p: "After each round, log a quick note on your phone. Every connection is waiting in your inbox the next morning." },
];

const CATS = [
  ["IT & Software",         "Automation · AI"],
  ["Manufacturing",         "Textiles · Engineering"],
  ["Healthcare",            "Clinics · Diagnostics"],
  ["Finance",               "Banking · Insurance"],
  ["Real Estate",           "Construction"],
  ["Retail",                "Trade · Distribution"],
  ["Hospitality",           "Food · Events"],
  ["Professional Services", "Legal · Consulting"],
];

const TRAVEL = [
  ["Getting to Davangere",           "Nearest airports, rail connections, and recommended arrival windows for the morning of the event."],
  ["Venue: Trishul Kalaabhavana",    "KB Extension, Davangere, Karnataka 577002. Open in Google Maps →"],
  ["Where to stay",                  "A shortlist of partner hotels near the venue, with a region-negotiated rate for visiting and international members."],
  ["Getting around",                 "Shared cab coordination between the venue, your hotel, and the airport/station — before and after the event."],
];

const FAQS = [
  ["How does the one-to-one conclave scheduling work?",  "You'll select your interests at registration. A personal schedule is generated and sent to you before the event — no manual sign-up sheets on the day."],
  ["I'm coming from Cross Region — what's included?", "Visiting and international members receive a travel note with arrival guidance, partner hotel options, and a departure slip for after the event."],
  ["Is there a dress code?",                              "Business formal for all sessions. Details on any theme for the cultural evening will be shared closer to the date."],
  ["Who do I contact for help on the day?",              "Your confirmation includes a dedicated helpdesk contact and WhatsApp broadcast group for real-time updates throughout the day."],
];

// -------------- Main page --------------
export default function PublicRegistration() {
  const [locale, setLocale] = useState(() => {
    try { return localStorage.getItem("bni-md-locale") || "en"; } catch { return "en"; }
  });
  const t = COPY[locale];
  useEffect(() => { try { localStorage.setItem("bni-md-locale", locale); } catch {} }, [locale]);

  const [shareOpen, setShareOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [mode, setMode] = useState("member");
  const [step, setStep] = useState("form");
  const { data: pub, error: pubError } = usePublicEvent();
  useEffect(() => { track("landing_visit", EVENT_SLUG); }, []);
  const TIERS = useMemo(() => (pub?.tiers || []).filter((t) => t.kind === "member"), [pub]);
  const STALL_TIERS = useMemo(() => (pub?.tiers || []).filter((t) => t.kind === "stall"), [pub]);
  const SPONSOR_TIERS = useMemo(() => (pub?.tiers || []).filter((t) => t.kind === "sponsor"), [pub]);
  const CATEGORIES = pub?.business_categories || [];
  const [selectedTier, setSelectedTier] = useState("");
  const [selectedStall, setSelectedStall] = useState("");
  const [selectedSponsor, setSelectedSponsor] = useState("");
  const [sponsorAmount, setSponsorAmount] = useState("");
  const [dirChapters, setDirChapters] = useState(null);
  // Region → chapters catalog (BNI South Karnataka) + "Other" for members outside the list.
  // Loaded once on mount. form.region === "__other__" unlocks the free-text region+chapter inputs.
  const [regionsCatalog, setRegionsCatalog] = useState([]);
  useEffect(() => { publicApi.regions().then(setRegionsCatalog).catch(() => setRegionsCatalog([])); }, []);
  const [dirMembers, setDirMembers] = useState([]);
  const [sponsorWall, setSponsorWall] = useState([]);
  const [phoneToken, setPhoneToken] = useState(null);
  const [emailToken, setEmailToken] = useState(null);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const agendaSectionRef = React.useRef(null);
  // Auto-collapse when the agenda scrolls out of view (user keeps browsing).
  useEffect(() => {
    if (!agendaOpen) return;
    const el = agendaSectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { for (const e of entries) if (!e.isIntersecting) setAgendaOpen(false); },
      { threshold: 0, rootMargin: "0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [agendaOpen]);
  useEffect(() => {
    if (TIERS.length && !selectedTier) setSelectedTier(TIERS[0].id);
    if (STALL_TIERS.length && !selectedStall) setSelectedStall(STALL_TIERS[0].id);
    if (SPONSOR_TIERS.length && !selectedSponsor) setSelectedSponsor(SPONSOR_TIERS[0].id);
    if (CATEGORIES.length && !CATEGORIES.includes(form.category) && form.category !== "" && form.category !== "__other__") setForm((f) => ({ ...f, category: "" }));
  }, [TIERS, STALL_TIERS, SPONSOR_TIERS, CATEGORIES]); // eslint-disable-line

  const [reg, setReg] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");
  const [guard, setGuard] = useState(null);
  const [invitePhone, setInvitePhone] = useState("");
  const rzRef = React.useRef(null);
  const watchRef = React.useRef(null);
  const stopWatch = () => { if (watchRef.current) { clearInterval(watchRef.current); watchRef.current = null; } };
  useEffect(() => () => stopWatch(), []);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("resume");
    if (!id || !pub) return;
    publicApi.sync(id).then((r) => {
      if (r.status === "paid") { onPaid(r); return; }
      setReg({ id: r.id, name: r.name, phone: "", status: r.status, tier_id: r.tier?.id, photo_url: r.photo_url });
      setForm((f) => ({ ...f, name: r.name }));
      if (r.photo_url) setPhoto(r.photo_url);
      document.getElementById("register-anchor")?.scrollIntoView({ behavior: "smooth" });
      toast.info("Welcome back — your details are saved. Tap Confirm registration to finish paying.");
    }).catch(() => {});
    // eslint-disable-next-line
  }, [pub]);
  const [inviteState, setInviteState] = useState({ checking: false, denied: false, unlocked: false });
  const [paidReg, setPaidReg] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUploadedFor, setPhotoUploadedFor] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", region: "", chapter: "", regionOther: "", chapterOther: "", city: "",
    category: "", categoryOther: "", stallName: "", contactPerson: "",
    wantsGstInvoice: false, gstin: "", firmName: "",
  });
  // Member directory (DB-backed roster for the selected region+chapter). Drives the
  // searchable name picker and auto-fills + locks the Business category.
  const [memberDirectory, setMemberDirectory] = useState([]);
  const [memberId, setMemberId] = useState(null);
  const [memberQuery, setMemberQuery] = useState("");
  const [memberOpen, setMemberOpen] = useState(false);
  const isDirectoryMember = form.region && form.region !== "__other__" && !!memberId;
  useEffect(() => {
    if (form.region && form.region !== "__other__" && form.chapter) {
      publicApi.directoryMembers(form.region, form.chapter)
        .then(setMemberDirectory).catch(() => setMemberDirectory([]));
    } else {
      setMemberDirectory([]);
    }
    setMemberId(null); setMemberQuery("");
  }, [form.region, form.chapter]);
  const filteredMembers = useMemo(() => {
    if (!memberQuery.trim()) return memberDirectory.slice(0, 100);
    const q = memberQuery.toLowerCase();
    return memberDirectory.filter((m) => m.name.toLowerCase().includes(q)).slice(0, 100);
  }, [memberQuery, memberDirectory]);
  const pickMember = (m) => {
    setMemberId(m.id); setMemberQuery(m.name); setMemberOpen(false);
    // Pre-fill name + category + on-file phone/email. User can still edit any field,
    // then verify via OTP as usual. If the directory doesn't have a phone/email on
    // record we leave the field untouched so a prior value isn't wiped. Reset any
    // prior verification tokens — the autofilled values must be verified fresh.
    setForm((f) => {
      const next = { ...f, name: m.name, category: m.category || "", categoryOther: "" };
      if (m.phone) {
        const digits = String(m.phone).replace(/\D/g, "").slice(-10);
        if (digits && digits.length === 10) next.phone = digits;
      }
      if (m.email) next.email = String(m.email).trim();
      return next;
    });
    setPhoneToken(null);
    setEmailToken(null);
  };
  const clearMemberPick = () => {
    setMemberId(null); setMemberQuery("");
    setForm((f) => ({ ...f, name: "", category: "", categoryOther: "" }));
  };
  // Must be declared after `form` (TDZ) — chapters for the currently selected catalog region.
  const chaptersForRegion = useMemo(() => {
    const row = regionsCatalog.find((r) => r.region === form.region);
    return row ? row.chapters : [];
  }, [regionsCatalog, form.region]);
  const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
  const gstinValid = GSTIN_RE.test((form.gstin || "").trim().toUpperCase());
  const gstReady = !form.wantsGstInvoice || (gstinValid && form.firmName.trim().length >= 2);
  const nav = useNavigate();
  const { dispatch } = useDemo();

  // Sponsor flow: chapters + members come from the uploaded master member directory
  useEffect(() => {
    if (mode !== "sponsor" || dirChapters !== null) return;
    publicApi.chapters().then(setDirChapters).catch(() => setDirChapters([]));
  }, [mode, dirChapters]);
  useEffect(() => {
    if (mode !== "sponsor" || !form.chapter) return;
    publicApi.chapterMembers(form.chapter).then(setDirMembers).catch(() => setDirMembers([]));
  }, [mode, form.chapter]);
  useEffect(() => { publicApi.sponsors().then(setSponsorWall).catch(() => {}); }, []);

  const tier  = TIERS.find((t) => t.id === selectedTier);
  const stall = STALL_TIERS.find((s) => s.id === selectedStall);
  const sponsorTier = SPONSOR_TIERS.find((s) => s.id === selectedSponsor);
  const activeTier = mode === "member" ? tier : mode === "stall" ? stall : sponsorTier;
  // Custom sponsorship is priced from the typed amount; fixed tiers come from the server quote
  const sponsorCustomTotal = useMemo(() => {
    const rs = parseInt(sponsorAmount || "0", 10);
    if (!rs || !pub) return 0;
    const gst = Math.round(rs * 100 * (pub.pricing.gst_pct || 18) / 100);
    return rs * 100 + gst;
  }, [sponsorAmount, pub]);
  // Host-region surcharge (members from the event's host region pay a premium — e.g. ₹500).
  // Keeps the UI total in sync with backend `quote()` which applies the same rule server-side.
  // Must compare against the EFFECTIVE region (regionOther when the user picks "Other" and
  // types a host-region name like "Davangere") — otherwise the UI total diverges from the
  // amount Razorpay charges.
  const hostRegionLc = (pub?.event?.region || "").trim().toLowerCase();
  const effectiveRegion = form.region === "__other__" ? form.regionOther : form.region;
  const hostSurchargePaise = useMemo(() => {
    if (mode !== "member" || !activeTier) return 0;
    if (!hostRegionLc) return 0;
    if ((effectiveRegion || "").trim().toLowerCase() !== hostRegionLc) return 0;
    return 500 * 100;
  }, [mode, activeTier, hostRegionLc, effectiveRegion]);
  const totalPaise = mode === "sponsor" && sponsorTier?.custom ? sponsorCustomTotal : ((activeTier?.total_paise || 0) + hostSurchargePaise);
  const amount = totalPaise / 100;
  const displayName = mode === "member" ? (form.name || "Member") : mode === "stall" ? (form.stallName || "Stall") : (form.name || "Sponsor");
  const chapters = useMemo(() => CHAPTERS.filter((c) => c.region === form.region), [form.region]);

  const openCheckout = async (r) => {
    setPaying(true); setPayError(""); setGuard(null);
    try {
      const order = await publicApi.createOrder(r.id);
      const Razorpay = await loadRazorpay();
      const rz = new Razorpay({
        key: order.key_id, order_id: order.order_id, amount: order.amount_paise, currency: order.currency,
        name: pub.event.name, description: `${activeTier?.name || "Members Day pass"} · incl. GST`, theme: { color: "#CC252F" },
        prefill: { name: r.name, contact: r.phone || form.phone, email: form.email || undefined },
        notes: { registration_id: r.id },
        handler: async (resp) => {
          try {
            await publicApi.verify(r.id, resp);
            const final = await pollUntilPaid(r.id);
            if (final.status !== "paid") { setPayError("Payment received but confirmation is still pending — we'll WhatsApp you once it clears."); setPaying(false); return; }
            onPaid(final);
          } catch (err) { setPayError(apiError(err)); setPaying(false); }
        },
        modal: { ondismiss: () => { stopWatch(); setPaying(false); setPayError("Payment window closed. Your registration is saved — retry when ready."); } },
      });
      rz.on("payment.failed", (resp) => {
        stopWatch(); setPaying(false);
        setPayError(resp?.error?.description || "Payment failed. You can retry.");
        publicApi.sync(r.id).then((s) => { if (s.payment_error?.friendly) setPayError(s.payment_error.friendly); }).catch(() => {});
      });
      rz.open();
      // UPI / QR flows often never fire the browser callback — ask our server (which asks Razorpay) every 4s
      rzRef.current = rz;
      watchRef.current = setInterval(async () => {
        try {
          const s = await publicApi.sync(r.id);
          if (s.status === "paid") { stopWatch(); try { rz.close(); } catch (e) { /* already closed */ } onPaid(s); }
          else if (s.payment_error?.friendly) { stopWatch(); try { rz.close(); } catch (e) { /* already closed */ } setPaying(false); setPayError(s.payment_error.friendly); }
        } catch (e) { /* transient */ }
      }, 4000);
    } catch (err) { stopWatch(); setPaying(false); setPayError(apiError(err)); }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (mode === "member" && !form.name) return toast.error("Please enter your name");
    if (mode === "stall" && !form.stallName) return toast.error("Please enter stall name");
    if (mode === "sponsor") {
      if (!form.chapter) return toast.error("Please select your chapter");
      if (!form.name) return toast.error("Please select or enter your name");
      if (sponsorTier?.custom && (!sponsorAmount || parseInt(sponsorAmount, 10) < (sponsorTier.min_fee || 1)))
        return toast.error(`Minimum custom sponsorship is ₹${(sponsorTier.min_fee || 0).toLocaleString("en-IN")}`);
    }
    if (mode === "member") {
      const effRegion  = form.region === "__other__" ? form.regionOther.trim()  : form.region.trim();
      const effChapter = form.region === "__other__" ? form.chapterOther.trim() : form.chapter.trim();
      if (!effRegion) return toast.error("Please select or enter your region");
      if (!effChapter) return toast.error("Please select or enter your chapter");
      // For cataloged chapters, require picking from the directory — "Other" is the free-text escape hatch.
      if (form.region !== "__other__" && form.chapter && memberDirectory.length > 0 && !memberId) {
        return toast.error("Please pick your name from the directory, or choose 'Other' as the region if you are not listed.");
      }
      if (!form.category) return toast.error("Please select your business category");
      if (form.category === "__other__" && !form.categoryOther.trim()) return toast.error("Please specify your business category");
      // Warn (not block) on phone/email mismatch with directory record
      if (memberId) {
        try {
          const r = await publicApi.directoryCheckContact({ member_id: memberId, phone: form.phone, email: form.email });
          if (r && r.warn) {
            const parts = [];
            if (!r.phone_matches) parts.push("phone");
            if (!r.email_matches) parts.push("email");
            toast.warning(`The ${parts.join(" and ")} you entered doesn't match our directory for ${form.name}. Please re-check before continuing.`);
          }
        } catch (_) { /* non-fatal — never block registration on a directory check */ }
      }
      if (form.wantsGstInvoice) {
        if (!gstinValid) return toast.error("Please enter a valid 15-character GSTIN");
        if (form.firmName.trim().length < 2) return toast.error("Please enter the firm name as per your GST certificate");
      }
    }
    if (!form.email.trim()) return toast.error("Please enter your email — your invoice is sent there");
    const vcfg = pub?.contact_verification || {};
    if (vcfg.phone_required && !phoneToken) return toast.error("Please verify your WhatsApp number");
    if (vcfg.email_required && !emailToken) return toast.error("Please verify your email address");
    if (!photoFile && !reg?.photo_url && !photo) return toast.error(mode === "stall" ? "Please upload your stall logo" : "Please upload your photo");
    if (!activeTier) return toast.error("Tiers are still loading");
    track("payment_initiated", EVENT_SLUG);
    setPaying(true); setPayError(""); setGuard(null);
    try {
      // Reuse the existing draft/pending registration on retry — never create a duplicate
      let r = reg && reg.tier_id === activeTier.id && reg.status !== "paid" ? reg : null;
      if (!r) {
        r = await publicApi.createRegistration({
          event_id: pub.event.id, registration_type: mode, tier_id: activeTier.id,
          name: mode === "stall" ? form.stallName : form.name, phone: form.phone, email: form.email.trim(),
          business_category: form.category === "__other__" ? form.categoryOther.trim() : form.category, chapter: mode === "stall" ? null : (form.region === "__other__" ? form.chapterOther.trim() : form.chapter), region: mode === "stall" ? null : (form.region === "__other__" ? form.regionOther.trim() : form.region.trim()), city: form.city, country: "India",
          ...(mode === "member" && form.wantsGstInvoice ? { gstin: form.gstin.trim().toUpperCase(), firm_name: form.firmName.trim() } : {}),
          ...(mode === "sponsor" && sponsorTier?.custom ? { sponsor_amount: parseInt(sponsorAmount, 10) } : {}),
          phone_token: phoneToken, email_token: emailToken,
          custom_fields: mode === "stall" ? { contact_person: form.contactPerson } : {},
        });
        setReg(r);
      } else if (mode === "member") {
        // Resumed draft: push latest GST toggle to the server so the invoice reflects the final choice.
        try {
          await publicApi.updateBilling(r.id, {
            wants_gst: !!form.wantsGstInvoice,
            gstin: form.wantsGstInvoice ? form.gstin.trim().toUpperCase() : null,
            firm_name: form.wantsGstInvoice ? form.firmName.trim() : null,
          });
        } catch (_) { /* non-fatal — payment can still proceed with existing billing */ }
      }
      // Always attach the chosen photo to the live registration — also on retries/resumes
      if (photoFile && photoUploadedFor !== r.id) {
        try { await publicApi.uploadPhoto(r.id, photoFile); setPhotoUploadedFor(r.id); }
        catch (e) { toast.error("Photo couldn't be saved — continuing with your registration"); }
      }
      await openCheckout(r);
    } catch (err) {
      setPaying(false);
      const g = guardrail(err);
      if (g) { setGuard(g); setPayError(""); return; }
      setPayError(apiError(err)); toast.error(apiError(err));
    }
  };

  const resumeFromGuard = async () => {
    const res = guard?.resume;
    if (!res) return;
    if (res.paid) { setGuard({ ...guard, message: "Good news — this number is already registered and paid. Check your WhatsApp for the pass, or log in to view it." }); return; }
    const r = { id: res.registration_id, name: res.name, phone: form.phone, status: res.status, tier_id: res.tier_id };
    setReg(r);
    await openCheckout(r);
  };

  const onPaid = (final) => {
    setPaidReg(final); setPaying(false);
    const isStall = mode === "stall";
    const effRegion  = form.region === "__other__" ? form.regionOther  : form.region;
    const effChapter = form.region === "__other__" ? form.chapterOther : form.chapter;
    dispatch({ type: "REGISTER_MEMBER", member: {
      id: final.id, name: isStall ? (form.contactPerson || form.stallName) : form.name, phone: form.phone, email: form.email,
      chapter: effChapter, region: effRegion, category: form.category, tier: activeTier?.id, tierName: activeTier?.name || final.tier?.name,
      fee: final.payment.amount_paise / 100, photo, mode, stallName: isStall ? form.stallName : undefined, contactPerson: isStall ? form.contactPerson : undefined,
    } });
    nav(`/success/${final.id}`);
  };

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "var(--panel)", border: "1px solid var(--hairline)", color: "var(--text)", borderRadius: 0 }
      }} />

      <RegisterNav t={t} locale={locale} setLocale={setLocale} onLogin={() => setLoginOpen(true)} />

      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />

      {/* Hero */}
      <section className="reg-hero" onMouseMove={(e) => {
        const el = e.currentTarget;
        const r = el.getBoundingClientRect();
        el.style.setProperty("--mx", `${e.clientX - r.left}px`);
        el.style.setProperty("--my", `${e.clientY - r.top}px`);
        el.classList.add("lume-on");
      }} onMouseLeave={(e) => e.currentTarget.classList.remove("lume-on")}>
        <div className="cursor-lume" aria-hidden="true" />
        <div className="wrap-x reg-hero-inner">
          <motion.h1
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="reg-title"
          >
            {t.titleLine1}<br />
            <span className="line-2"><span className="accent">{t.titleAccent}</span> {t.titleRest}</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.12 }}
            className="reg-slug"
            data-testid="hero-slug"
          >
            <span className="reg-slug-rule" aria-hidden="true" />
            <span className="reg-slug-text">
              {t.slugParts.map(([w, c], i) => <React.Fragment key={i}>{i > 0 && " "}<span className={`slug-w ${c === "w" ? "slug-white" : "slug-red"}`}>{w}</span></React.Fragment>)}
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}
            className="lede"
          >
            {t.lede}
          </motion.p>

          <div className="pillars">
            <div className="pillar">
              <div className="lab">{t.pDate}</div>
              <div className="val">{t.vDate}</div>
            </div>
            <div className="pillar">
              <div className="lab">{t.pVenue}</div>
              <div className="val">{t.vVenue}</div>
            </div>
            <div className="pillar pillar-countdown">
              <Countdown target="2026-11-28T09:00:00+05:30" t={t} compact />
            </div>
          </div>

          <div className="ctas">
            <a data-testid="hero-register-btn"  href="#register-anchor" className="btn-red">
              <UserPlus size={16} /> <span>{t.ctaRegister}</span>
            </a>
            <a data-testid="hero-agenda-btn"
               href="/members-day-agenda.pdf"
               download="RISSE-BNI-2026-Agenda.pdf"
               target="_blank" rel="noopener"
               className="btn-ghost">
              <Download size={16} /> <span>{t.ctaAgenda}</span>
            </a>
            <button data-testid="hero-share-btn" onClick={() => setShareOpen(true)} className="share-btn">
              <Share2 size={16} /> <span>{t.ctaShare}</span>
            </button>
          </div>
        </div>
      </section>

      {/* About the Event */}
      <section id="about-anchor" className="editorial-section" data-testid="about-section">
        <div className="wrap-x">
          <div className="about-grid">
            <div className="about-leaders">
              <Eyebrow>About the Event</Eyebrow>
              <div className="about-leader-row" data-testid="about-leaders">
                {(pub?.guest_speakers?.length ? pub.guest_speakers : [
                  { id: "mac",  name: "Mac Srinivasan",      title: "Global Markets President, BNI",                     photo_url: "/guests/mac-srinivasan.png" },
                  { id: "raj",  name: "Rajendran Dandapani", title: "Business Solutions Evangelist, Zoho Corporation",   photo_url: "/guests/rajendran-dandapani.png" },
                ]).slice(0, 3).map((g) => (
                  <figure key={g.id || g.name} className="about-leader-card" data-testid={`leader-${(g.id || g.name).toLowerCase().replace(/\s+/g, "-")}`}>
                    <img src={g.photo_url} alt={g.name} />
                    <figcaption>
                      <div className="n">{g.name}</div>
                      <div className="t">{g.title}</div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>

            <div className="about-copy">
              <h2>Where Relationships Rise, Ideas Inspire &amp; Businesses Grow</h2>
              <p>RISSE BNI 2026 is a dynamic business networking event designed to bring together ambitious entrepreneurs, business leaders, professionals, and BNI members for a powerful experience of connection, collaboration, and growth.</p>
              <p>Built around BNI's philosophy of <em>"Givers Gain®,"</em> RISSE creates an environment where meaningful conversations turn into trusted relationships and relationships turn into real business opportunities. The event brings together diverse minds to exchange ideas, share experiences, discover new possibilities, and build stronger connections beyond their regular networks.</p>
              <p>Through inspiring interactions, purposeful networking, and shared learning, participants can expand their business reach, strengthen relationships, gain fresh perspectives, and create opportunities for long-term collaboration.</p>
              <p>RISSE BNI 2026 is more than an event — it is where connections rise, opportunities emerge, and businesses move forward together.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors marquee */}
      <div className="sponsors-strip" data-testid="sponsors-marquee">
        <div className="sponsors-eyebrow">
          <span className="line" aria-hidden="true" />
          <span className="label">Our Partners &amp; Sponsors</span>
          <span className="line" aria-hidden="true" />
        </div>

        {/* Main Sponsors — tiered, centred, above the scroll */}
        <div className="main-sponsors" data-testid="main-sponsors">
          <div className="main-sponsor-row" data-testid="row-title-sponsor">
            <div className="main-sponsor-tier">Title Sponsor</div>
            <div className="main-sponsor-cards">
              <div className="main-sponsor-card tier-title" data-testid="main-sponsor-shivex">
                <img src="/sponsors/shivex.png" alt="Shivex Fire Protection Services" />
              </div>
            </div>
          </div>

          <div className="main-sponsor-row" data-testid="row-powered-by">
            <div className="main-sponsor-tier">Powered By Sponsor</div>
            <div className="main-sponsor-cards">
              <div className="main-sponsor-card tier-powered" data-testid="main-sponsor-aakar">
                <img src="/sponsors/aakar-gpr.png" alt="Aakar GPR Works · Art-Master" />
              </div>
              <div className="main-sponsor-card tier-powered" data-testid="main-sponsor-veon">
                <img src="/sponsors/veon.png" alt="Veon · Vastu · Energy Optimisation · Numerology" />
              </div>
            </div>
          </div>

          <div className="main-sponsor-row" data-testid="row-branding-partner">
            <div className="main-sponsor-tier">Branding Partner</div>
            <div className="main-sponsor-cards">
              <div className="main-sponsor-card tier-branding" data-testid="main-sponsor-bcl">
                <img src="/sponsors/bcl.png" alt="BCL · Creative AD Agency" />
              </div>
            </div>
          </div>

          <div className="main-sponsor-row" data-testid="row-technology-partner">
            <div className="main-sponsor-tier">Technology Partner</div>
            <div className="main-sponsor-cards">
              <div className="main-sponsor-card tier-technical" data-testid="main-sponsor-zerolabs">
                <img src="/sponsors/zerolabs.png" alt="Zerolabs Digitals" />
              </div>
            </div>
          </div>
        </div>

        <div className="sponsors-marquee">
          <div className="sponsors-track">
            {(sponsorWall.length
              ? [...sponsorWall, ...sponsorWall].map((s) => ({ tier: s.tier, name: s.chapter ? `${s.name} · ${s.chapter}` : s.name }))
              : [...SPONSORS, ...SPONSORS].map((s) => ({ tier: s.tier, name: s.brand || s.name }))
            ).map((s, i) => (
              <div key={i} className="sponsor-tile" data-testid={`sponsor-${i}`}>
                <div className="sponsor-copy">
                  <div className="sponsor-tier">{s.tier}</div>
                  <div className="sponsor-name">{s.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sponsors-footer">
          {sponsorWall.length
            ? `Thank you to our ${sponsorWall.length} sponsor${sponsorWall.length > 1 ? "s" : ""} — plus Shivex, Aakar GPR Works, Veon, BCL and Zerolabs Digitals.`
            : "Our heartfelt thanks to Shivex, Aakar GPR Works, Veon, BCL and Zerolabs Digitals for powering RISSE BNI 2026."}
        </div>
      </div>

      {/* Agenda */}
      <section id="agenda-anchor" ref={agendaSectionRef} className="editorial-section">
        <div className="wrap-x">
          <button
            type="button"
            data-testid="agenda-toggle"
            onClick={() => setAgendaOpen((v) => !v)}
            aria-expanded={agendaOpen}
            aria-controls="agenda-content"
            className="section-head agenda-head-btn"
          >
            <div>
              <Eyebrow>The Day</Eyebrow>
              <h2>One day, start to finish.</h2>
              <p>Every session, every timing — so you know exactly where to be. Full details are sent to you after registration, with reminders as each session approaches.</p>
            </div>
            <span className={`agenda-chevron ${agendaOpen ? "is-open" : ""}`} aria-hidden="true">
              <ChevronDown size={22} />
            </span>
          </button>
          <AnimatePresence initial={false}>
            {agendaOpen && (
              <motion.div
                id="agenda-content"
                data-testid="agenda-content"
                key="agenda"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ height: { duration: 0.42, ease: [0.22, 1, 0.36, 1] }, opacity: { duration: 0.28 } }}
                style={{ overflow: "hidden" }}
              >
                <AgendaRail />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <div className="thread" />

      {/* Conclave */}
      <section id="conclave-anchor" className="editorial-section">
        <div className="wrap-x">
          <div className="section-head">
            <Eyebrow>The Centrepiece</Eyebrow>
            <h2>The One-to-One Conclave</h2>
            <p>Structured, time-boxed one-to-ones designed so every member — local, outstation, or international — leaves having met the right people, not just more people.</p>
          </div>

          <div className="weave-grid mt-10">
            {WEAVE.map((w) => (
              <div key={w.n} className="weave-cell">
                <div className="n">{w.n}</div>
                <h4>{w.h}</h4>
                <p>{w.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="thread" />

      {/* Why Attend this Event */}
      <section id="why-attend-anchor" className="editorial-section" data-testid="why-attend-section">
        <div className="wrap-x">
          <div className="section-head" style={{ textAlign: "center", margin: "0 auto" }}>
            <Eyebrow>Why Attend</Eyebrow>
            <h2>Why Attend this Event</h2>
          </div>

          <div className="weave-grid mt-10">
            <div className="weave-cell" data-testid="why-attend-expo">
              <Telescope size={28} style={{ color: "var(--red)" }} />
              <h4>Explore the Business Expo</h4>
              <p>Discover a wide range of businesses, products, and services all in one place. The Expo is your chance to interact directly with brands, understand what they offer, and find potential partners or solutions for your own business. A perfect entry point to experience the RISSE BNI ecosystem.</p>
            </div>
            <div className="weave-cell" data-testid="why-attend-experience">
              <Handshake size={28} style={{ color: "var(--red)" }} />
              <h4>Experience BNI Like Never Before</h4>
              <p>Step into the world of RISSE BNI and witness how powerful business networking truly works. Visitors get exclusive access to live chapter meetings where real business happens, connections are built, and opportunities are created. This is not just an event — it's an experience.</p>
            </div>
            <div className="weave-cell" data-testid="why-attend-opportunities">
              <Users size={28} style={{ color: "var(--red)" }} />
              <h4>Turn Conversations into Business Opportunities</h4>
              <p>Meet serious business owners, decision-makers, and entrepreneurs under one roof. Whether you attend the Expo or the Chapter Meeting, every interaction has the potential to open doors, generate referrals, and create meaningful collaborations that drive real growth.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="thread" />

      {/* Register */}
      <section id="register-anchor" className="editorial-section !pb-6 sm:!pb-8">
        <div className="wrap-x">
          {step === "form" && (
            <div className="reg-grid">
              {/* Left column: heading + tiers */}
              <div>
                <div className="section-head">
                  <Eyebrow>Reserve Your Place</Eyebrow>
                  <h2>Register for Members Day</h2>
                  <p>Choose the pass that fits you. Every tier includes the full-day agenda, meals, and your personal one-to-one conclave schedule.</p>
                </div>

                <div className="mt-8 reg-toggle" data-mode={mode} role="tablist" aria-label="Registration type">
                  <span className="reg-toggle-bubble" aria-hidden="true" />
                  <button
                    type="button"
                    data-testid="mode-toggle-member"
                    role="tab"
                    aria-selected={mode === "member"}
                    onClick={() => setMode("member")}
                    className={`reg-toggle-pill ${mode === "member" ? "active" : ""}`}
                  >
                    <User size={14} /> <span>Member</span>
                  </button>
                  <button
                    type="button"
                    data-testid="mode-toggle-stall-sponsor"
                    role="tab"
                    aria-selected={mode === "contact"}
                    onClick={() => setMode("contact")}
                    className={`reg-toggle-pill ${mode === "contact" ? "active" : ""}`}
                  >
                    <Store size={14} /> <span>Stall &amp; Sponsor</span>
                  </button>
                </div>

                {mode === "sponsor" && SPONSOR_TIERS.length > 0 && (
                  <div className="mt-8 space-y-2" data-testid="sponsor-tier-container">
                    <div className="mono-label">Choose your sponsorship</div>
                    {SPONSOR_TIERS.map((t) => (
                      <button type="button" key={t.id} data-testid={`sponsor-tier-${t.id}`}
                        onClick={() => setSelectedSponsor(t.id)}
                        className={`w-full text-left hairline p-4 transition-colors ${selectedSponsor === t.id ? "ring-1" : ""}`}
                        style={selectedSponsor === t.id ? { borderColor: "var(--red)", background: "var(--panel-2)" } : {}}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm">{t.name}</div>
                            <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{t.desc}</div>
                          </div>
                          <div className="font-serif text-lg shrink-0" style={{ color: "var(--red-2)" }}>
                            {t.custom ? "Your amount" : `₹${(t.base_fee || 0).toLocaleString("en-IN")}`}
                          </div>
                        </div>
                      </button>
                    ))}
                    <div className="mono-label pt-2" style={{ color: "var(--muted)" }}>
                      GST {pub?.pricing?.gst_pct ?? 18}% is added at checkout and a tax invoice is emailed to you.
                    </div>
                  </div>
                )}

                {pubError && <div data-testid="tiers-error" className="mt-6 text-sm" style={{ color: "var(--red-2)" }}>Could not load tiers — {apiError(pubError)}</div>}
                {!pub && !pubError && <div data-testid="tiers-loading" className="mt-6 mono-label">Loading live pricing…</div>}
              </div>

              {/* Right column: form (or contact card for Stall & Sponsor) */}
              <div>
                {mode === "contact" ? (
                  <div className="reg-form" data-testid="stall-sponsor-contact-card">
                    <Eyebrow>Stall &amp; Sponsor</Eyebrow>
                    <h3 className="font-serif text-2xl mt-3">Let's talk directly.</h3>
                    <p className="text-sm mt-4" style={{ color: "var(--taupe)", whiteSpace: "pre-line" }} data-testid="stall-sponsor-contact-message">
                      {(pub?.register_contact_message || "For Stall and Sponsor registrations, please contact\n+91 94495 81062").replace(/\s*(\+?\d[\d\s\-]{7,})/, "\n$1")}
                    </p>
                    {(() => {
                      const msg = pub?.register_contact_message || "For Stall and Sponsor registrations, please contact +91 94495 81062";
                      const phoneMatch = msg.match(/(\+?\d[\d\s\-]{7,})/);
                      if (!phoneMatch) return null;
                      const digits = phoneMatch[1].replace(/[^\d]/g, "");
                      const wa = digits.length === 10 ? `91${digits}` : digits;
                      return (
                        <div className="mt-6 cta-pair" data-testid="stall-sponsor-cta-pair">
                          <a data-testid="stall-sponsor-call-btn" href={`tel:+${wa}`} className="cta-pill cta-pill-primary">
                            Call now
                          </a>
                        </div>
                      );
                    })()}
                  </div>
                ) : pub?.soft_launch?.enabled && !inviteState.unlocked ? (
                  <div className="reg-form" data-testid="soft-launch-gate">
                    <div className="mono-label" style={{ color: "var(--red-2)" }}>Invite-only pilot</div>
                    <h3 className="font-serif text-2xl mt-3">Registration opens soon</h3>
                    <p className="text-sm mt-3" style={{ color: "var(--taupe)" }}>{pub.soft_launch.message}</p>
                    <div className="hairline-t mt-6 pt-5">
                      <label className="field-label">
                        <span>Got an invite? Enter your WhatsApp number</span>
                        <input data-testid="invite-phone-input" type="tel" className="input-x" value={invitePhone}
                          placeholder="+91 98765 43210"
                          onChange={(e) => { setInvitePhone(e.target.value); setInviteState((s) => ({ ...s, denied: false })); }} />
                      </label>
                      <button type="button" data-testid="invite-unlock-btn" className="btn-red mt-4" disabled={inviteState.checking}
                        onClick={async () => {
                          setInviteState({ checking: true, denied: false, unlocked: false });
                          try {
                            const res = await publicApi.checkSoftLaunch(pub.event.id, invitePhone);
                            if (res.allowed) {
                              setForm((f) => ({ ...f, phone: invitePhone }));
                              setInviteState({ checking: false, denied: false, unlocked: true });
                              toast.success("Invite confirmed — your form is unlocked");
                            } else {
                              setInviteState({ checking: false, denied: true, unlocked: false });
                            }
                          } catch (err) { setInviteState({ checking: false, denied: false, unlocked: false }); toast.error(apiError(err)); }
                        }}>
                        {inviteState.checking ? "Checking…" : "Unlock my form"} <ArrowRight size={14} />
                      </button>
                      {inviteState.denied && (
                        <div data-testid="invite-denied" className="mt-4 p-3 hairline text-sm" style={{ color: "var(--red-2)" }}>
                          This number isn't on the pilot list yet. We'll WhatsApp you the moment public registration opens.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                <form onSubmit={submitForm} className="reg-form">
                  {mode === "member" && (
                    <>
                      <div className="field-row">
                        <label className="field-label">
                          <span>Region</span>
                          <select data-testid="field-region" required className="input-x" value={form.region}
                            onChange={(e) => setForm({ ...form, region: e.target.value, chapter: "", chapterOther: "", regionOther: e.target.value === "__other__" ? form.regionOther : "" })}>
                            <option value="" disabled>Please select</option>
                            {regionsCatalog.map((r) => <option key={r.region} value={r.region}>{r.region}</option>)}
                            <option value="__other__">Other</option>
                          </select>
                        </label>
                        <label className="field-label">
                          <span>Chapter</span>
                          <select data-testid="field-chapter" required={form.region !== "__other__"}
                            disabled={!form.region || form.region === "__other__"} className="input-x" value={form.chapter}
                            onChange={(e) => setForm({ ...form, chapter: e.target.value })}>
                            <option value="" disabled>{form.region ? (form.region === "__other__" ? "Entered above" : "Please select") : "Select region first"}</option>
                            {chaptersForRegion.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </label>
                      </div>
                      {form.region === "__other__" && (
                        <div className="field-row">
                          <label className="field-label">
                            <span>Your region</span>
                            <input data-testid="field-region-other" type="text" required className="input-x" value={form.regionOther}
                              placeholder="e.g. Bangalore"
                              onChange={(e) => setForm({ ...form, regionOther: e.target.value })} />
                          </label>
                          <label className="field-label">
                            <span>Your chapter</span>
                            <input data-testid="field-chapter-other" type="text" required className="input-x" value={form.chapterOther}
                              placeholder="e.g. BNI Legends"
                              onChange={(e) => setForm({ ...form, chapterOther: e.target.value })} />
                          </label>
                        </div>
                      )}
                      <div className="field-row">
                        <label className="field-label relative">
                          <span>Full name</span>
                          {form.region !== "" && form.region !== "__other__" && form.chapter ? (
                            // Directory-backed: searchable member picker. Locks the member identity
                            // and auto-fills Business category below.
                            <>
                              <input
                                data-testid="field-member-search" type="text" required
                                className="input-x" placeholder="Type to search your name"
                                value={memberQuery}
                                onFocus={() => setMemberOpen(true)}
                                onBlur={() => setTimeout(() => setMemberOpen(false), 150)}
                                onChange={(e) => { setMemberQuery(e.target.value); setMemberId(null); setMemberOpen(true); setForm((f) => ({ ...f, name: e.target.value, category: "" })); }}
                                autoComplete="off"
                              />
                              {memberOpen && filteredMembers.length > 0 && (
                                <ul
                                  data-testid="field-member-dropdown"
                                  className="absolute z-50 left-0 right-0 top-full mt-1 max-h-60 overflow-auto rounded-md shadow-xl"
                                  style={{ background: "var(--surface-2, #121418)", border: "1px solid var(--hairline)" }}
                                >
                                  {filteredMembers.map((m) => (
                                    <li key={m.id}>
                                      <button
                                        type="button"
                                        data-testid={`field-member-opt-${m.id}`}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => pickMember(m)}
                                        className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm"
                                        style={{ color: "var(--body)" }}
                                      >
                                        <span className="block">{m.name}</span>
                                        <span className="block text-[11px] mt-0.5" style={{ color: "var(--taupe)" }}>{m.category || "—"}</span>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {memberId && (
                                <button
                                  type="button" data-testid="field-member-clear"
                                  onClick={clearMemberPick}
                                  className="absolute right-2 top-[34px] text-[11px] mono-label"
                                  style={{ color: "var(--taupe)" }}
                                >change</button>
                              )}
                            </>
                          ) : (
                            <input data-testid="field-name" type="text" required className="input-x" value={form.name}
                              onChange={(e) => setForm({ ...form, name: e.target.value })} onFocus={() => track("form_started", EVENT_SLUG)} />
                          )}
                        </label>
                        <label className="field-label">
                          <span>Business category</span>
                          {isDirectoryMember ? (
                            <input data-testid="field-category-locked" type="text" readOnly disabled
                              className="input-x opacity-80 cursor-not-allowed"
                              value={form.category || "—"} />
                          ) : (
                            <select data-testid="field-category" required className="input-x" value={form.category}
                              onChange={(e) => setForm({ ...form, category: e.target.value, categoryOther: e.target.value === "__other__" ? form.categoryOther : "" })}>
                              <option value="" disabled>Please select</option>
                              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                              <option value="__other__">Others</option>
                            </select>
                          )}
                        </label>
                      </div>
                      {form.category === "__other__" && (
                        <label className="field-label" style={{ marginBottom: 16 }}>
                          <span>Please specify your category</span>
                          <input data-testid="field-category-other" type="text" required maxLength={80} className="input-x" value={form.categoryOther}
                            onChange={(e) => setForm({ ...form, categoryOther: e.target.value })} />
                        </label>
                      )}
                      <div className="field-row">
                        <VerifiedField channel="phone" label="Phone / WhatsApp" testId="field-phone" eventId={pub?.event?.id}
                          value={form.phone} onChange={(v) => setForm({ ...form, phone: v })}
                          token={phoneToken} onVerified={setPhoneToken}
                          inputProps={{ type: "tel", required: true }} />
                        <VerifiedField channel="email" label="Email" testId="field-email" eventId={pub?.event?.id}
                          value={form.email} onChange={(v) => setForm({ ...form, email: v })}
                          token={emailToken} onVerified={setEmailToken}
                          inputProps={{ type: "email", required: true }} />
                      </div>
                      <div className="flex items-center gap-3 mt-1" style={{ marginBottom: 12 }}>
                        <input
                          data-testid="field-wants-gst"
                          type="checkbox"
                          id="wants-gst-cb"
                          className="w-4 h-4 accent-[var(--red)] cursor-pointer"
                          checked={form.wantsGstInvoice}
                          onChange={(e) => setForm({ ...form, wantsGstInvoice: e.target.checked, gstin: e.target.checked ? form.gstin : "", firmName: e.target.checked ? form.firmName : "" })}
                        />
                        <span className="text-sm" style={{ color: "var(--body)" }}>I want a GST invoice (B2B)</span>
                      </div>
                      {form.wantsGstInvoice && (
                        <div className="field-row" data-testid="gst-fields">
                          <label className="field-label">
                            <span>GSTIN<span style={{ color: "var(--red-2)", marginLeft: 4 }}>*</span></span>
                            <input
                              data-testid="field-gstin"
                              type="text"
                              required
                              maxLength={15}
                              className="input-x"
                              style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
                              value={form.gstin}
                              onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15) })}
                            />
                            <div className="mono-label mt-1" style={{ color: form.gstin && !gstinValid ? "var(--red-2)" : "var(--taupe)" }} data-testid="gstin-hint">
                              {form.gstin && !gstinValid ? "Invalid GSTIN format — must be 15 characters" : "15 chars · e.g. 29ABCDE1234F1Z5"}
                            </div>
                          </label>
                          <label className="field-label">
                            <span>Firm / Legal name<span style={{ color: "var(--red-2)", marginLeft: 4 }}>*</span></span>
                            <input
                              data-testid="field-firm-name"
                              type="text"
                              required
                              maxLength={160}
                              className="input-x"
                              value={form.firmName}
                              onChange={(e) => setForm({ ...form, firmName: e.target.value })}
                            />
                            <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>
                              Exactly as it appears on your GST certificate
                            </div>
                          </label>
                        </div>
                      )}
                    </>
                  )}

                  <div className="field-label" style={{ marginBottom: 16 }}>
                    <span>
                      Photo
                      <span style={{ color: "var(--red-2)", marginLeft: 4 }}>*</span>
                    </span>
                    <div className="flex items-center gap-4">
                      <div
                        className="w-16 h-16 border bg-[var(--panel-2)] overflow-hidden flex items-center justify-center"
                        style={{ borderColor: photo ? "var(--hairline)" : "rgba(226,62,71,0.45)" }}
                        data-testid="photo-upload-preview"
                      >
                        {photo ? <img src={photo} alt="upload" className="w-full h-full object-cover" /> : <Camera size={22} style={{ color: "var(--taupe)" }} />}
                      </div>
                      <label className="btn-ghost cursor-pointer !py-2 !px-3">
                        <Upload size={14} /> {photo ? "Replace" : "Upload"}
                        <input
                          data-testid="photo-upload-input"
                          type="file" accept="image/*" className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0]; if (!f) return;
                            if (f.size > 2 * 1024 * 1024) return toast.error("Photo is too large — please pick one under 2 MB");
                            setPhotoFile(f);
                            const r = new FileReader(); r.onload = () => setPhoto(r.result); r.readAsDataURL(f);
                          }}
                        />
                      </label>
                    </div>
                    <div className="mono-label mt-2" data-testid="photo-upload-hint" style={{ color: photo ? "var(--taupe)" : "var(--red-2)" }}>
                      {photo
                        ? "Photo attached · tap Replace to change"
                        : "Required · under 2 MB · used on your digital pass, big-screen reveals, and gallery search"}
                    </div>
                  </div>

                  <div className="hairline-t pt-5 flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <div className="mono-label">Total due</div>
                      <div className="font-serif text-3xl" data-testid="total-due">{totalPaise ? inr(totalPaise) : "—"}</div>
                      {mode === "member" && hostSurchargePaise > 0 && (
                        <div className="mono-label mt-1" data-testid="host-region-surcharge-note" style={{ color: "var(--taupe)" }}>
                          Includes ₹{(hostSurchargePaise / 100).toLocaleString("en-IN")} host-region fee · {pub?.event?.region} members
                        </div>
                      )}
                    </div>
                    <button type="submit" data-testid="register-submit-btn" className="btn-pill" disabled={paying || !activeTier || !totalPaise || (pub?.contact_verification?.phone_required && !phoneToken) || (pub?.contact_verification?.email_required && !emailToken) || (!photoFile && !photo && !reg?.photo_url) || !gstReady}>
                      {paying ? "Opening secure checkout…"
                        : reg && reg.status !== "paid" && payError ? "Retry payment"
                        : "Submit and pay"} <ArrowRight size={14} />
                    </button>
                  </div>
                  {guard && (
                    <div data-testid="registration-guardrail" className="mt-4 p-4 hairline text-sm" style={{ background: "var(--panel-2)" }}>
                      <div className="mono-label" style={{ color: "var(--red-2)" }}>One moment</div>
                      <p className="mt-2" style={{ color: "var(--text)" }}>{guard.message}</p>
                      {guard.resume && !guard.resume.paid && (
                        <button type="button" data-testid="guardrail-resume-btn" className="btn-red mt-4" disabled={paying} onClick={resumeFromGuard}>
                          Resume my registration <ArrowRight size={14} />
                        </button>
                      )}
                      {guard.resume?.paid && (
                        <div className="mono-label mt-3" style={{ color: "var(--taupe)" }}>
                          Registration {guard.resume.registration_id.slice(0, 8)} · already paid
                        </div>
                      )}
                      {!guard.resume && (
                        <div className="mono-label mt-3" style={{ color: "var(--taupe)" }}>
                          Need help now? WhatsApp the organising desk and we'll register you manually.
                        </div>
                      )}
                    </div>
                  )}
                  <AnimatePresence>
                    {payError && (
                      <motion.div data-testid="payment-error" className="pay-fail mt-5"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                        <div className="flex items-start gap-3">
                          <motion.span className="pay-fail-dot" aria-hidden="true"
                            initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: "spring", stiffness: 240, damping: 14 }} />
                          <div className="min-w-0">
                            <div className="mono-label" style={{ color: "var(--red-2)" }}>Payment didn't go through</div>
                            <p className="text-sm mt-2" style={{ color: "var(--text)" }}>{payError}</p>
                            <div className="mono-label mt-3" style={{ color: "var(--taupe)" }}>
                              Nothing was charged and your details are saved — retry below, or try a different method
                              (UPI, card, netbanking) on the next screen.
                            </div>
                            <div className="mt-5 flex flex-wrap gap-3">
                              <button type="submit" className="btn-pill small" data-testid="payfail-retry-btn" disabled={paying}>
                                {paying ? "Opening…" : "Try payment again"} <ArrowRight size={13} />
                              </button>
                              <a className="btn-ghost !py-2 !px-3 text-xs" data-testid="payfail-whatsapp-btn" target="_blank" rel="noreferrer"
                                href={`https://wa.me/918892210859?text=${encodeURIComponent(`Hi, my Members Day payment failed. Name: ${form.name || form.stallName}, phone: ${form.phone}`)}`}>
                                <MessageCircle size={13} /> WhatsApp the desk
                              </a>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
                )}
              </div>
            </div>
          )}

          <div
            className="mt-6 sm:mt-8 pb-[calc(env(safe-area-inset-bottom,0px)+70px)] sm:pb-0 text-center"
            style={{ color: "var(--taupe)", fontSize: "13px", fontWeight: 400 }}
            data-testid="registration-builtby"
          >
            Built by{" "}
            <a
              href="https://zerolabsdigi.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="registration-builtby-link"
              className="builtby-link"
            >
              Zerolabs Digitals
            </a>
          </div>
        </div>
      </section>

      <div className="thread" />

      {/* FAQ */}
      <section id="faq-anchor" className="editorial-section">
        <div className="wrap-x max-w-[820px] mx-auto">
          <div className="section-head">
            <Eyebrow>Good to Know</Eyebrow>
            <h2>Frequently asked questions</h2>
          </div>
          <div className="mt-8">
            {FAQS.map(([q, a], i) => <FaqItem key={i} q={q} a={a} />)}
          </div>
        </div>
      </section>

      <StickyCTA />
      <ShareCardModal open={shareOpen} onClose={() => setShareOpen(false)} t={t} />

      <footer className="site-footer hairline-t" data-testid="site-footer">
        <div className="wrap-x py-6 flex items-center justify-center">
          <div className="mono-label" style={{ color: "var(--taupe)" }}>
            Powered by{" "}
            <a
              href="https://zerolabsdigi.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-zerolabs-link"
              style={{ color: "var(--red-2)", textDecoration: "underline", textUnderlineOffset: "3px" }}
            >
              Zerolabs Digitals
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// -------------- Marketing nav on /register --------------
function smoothScroll(e, id) {
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  const navH = document.querySelector(".reg-nav")?.getBoundingClientRect().height || 0;
  const y = el.getBoundingClientRect().top + window.scrollY - navH - 12;
  window.scrollTo({ top: y, behavior: "smooth" });
  history.replaceState(null, "", `#${id}`);
}

function RegisterNav({ t, locale, setLocale, onLogin }) {
  const [scrollPct, setScrollPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const p = h > 0 ? Math.max(0, Math.min(100, (window.scrollY / h) * 100)) : 0;
      setScrollPct(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="scroll-bar" style={{ width: `${scrollPct}%` }} />
      <div className="reg-nav-wrap">
        <nav className="reg-nav" data-testid="register-nav">
          <div className="reg-nav-inner">
            <a href="#top" className="brand">
              <img src="/risse-bni-text.png" alt="RISSE 26 · BNI · REACH INSPIRE SYNERGIZE SUCCEED EXCEL · DAVANAGERE MEMBERS DAY"
                   className="wordmark" />
              <span className="div" />
              <img src="/risse-bni-logo.png" alt="RISSE 26 landmarks" className="logo" />
            </a>
            <div className="links">
              <a href="#agenda-anchor"   data-testid="nav-link-agenda"   onClick={(e) => smoothScroll(e, "agenda-anchor")}>{t.navAgenda}</a>
              <a href="#about-anchor"    data-testid="nav-link-about"    onClick={(e) => smoothScroll(e, "about-anchor")}>{t.navAbout}</a>
              <a href="#conclave-anchor" data-testid="nav-link-conclave" onClick={(e) => smoothScroll(e, "conclave-anchor")}>{t.navConclave}</a>
              <a href="#why-attend-anchor" data-testid="nav-link-why"    onClick={(e) => smoothScroll(e, "why-attend-anchor")}>{t.navWhy}</a>
              <a href="#faq-anchor"      data-testid="nav-link-faq"      onClick={(e) => smoothScroll(e, "faq-anchor")}>{t.navFaq}</a>
            </div>
            <div className="right-stack">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); onLogin?.(); }}
                className="cta cta-outline"
                data-testid="nav-login-btn"
                aria-label="Login"
              >
                <LogIn size={14} />
                <span className="cta-label">Login</span>
              </button>
              <a href="#register-anchor" className="cta" data-testid="nav-register-btn"
                 onClick={(e) => smoothScroll(e, "register-anchor")} aria-label={t.navRegister}>
                <UserPlus size={14} />
                <span className="cta-label">{t.navRegister}</span>
              </a>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}

// -------------- Seats-left live chip --------------
function SeatsLeftChip({ t }) {
  const left = Math.max(0, REG_TARGET - REG_PAID);
  const pct = (REG_PAID / REG_TARGET) * 100;
  if (pct < 90) return null;
  return (
    <div className="seats-chip" data-testid="seats-left-chip">
      <span className="dot" />
      <span>{t.seatsLeft} · {left.toLocaleString("en-IN")} / {REG_TARGET.toLocaleString("en-IN")}</span>
    </div>
  );
}

// -------------- WhatsApp share card modal --------------
function ShareCardModal({ open, onClose, t }) {
  const shareUrl  = "https://risse.bnidavangere.com/";
  // Keep the URL on its own line with a space-free prefix so WhatsApp always auto-links
  // it as a clickable hyperlink (previous builds occasionally produced "wordhttps://" when
  // text got concatenated without whitespace).
  const caption   = `${t.titleLine1} · ${t.titleAccent} ${t.titleRest}\n${t.vDate} · ${t.vVenue}\n\n${shareUrl}`;
  const waHref    = `https://wa.me/?text=${encodeURIComponent(caption)}`;

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(caption); toast.success("Caption copied · paste into WhatsApp"); }
    catch { toast.error("Copy failed"); }
  };

  const downloadSvg = () => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1350" viewBox="0 0 1080 1350">
<defs>
  <radialGradient id="g" cx="92%" cy="92%" r="80%">
    <stop offset="0%" stop-color="#E0A526" stop-opacity="0.32"/>
    <stop offset="50%" stop-color="#E0A526" stop-opacity="0.06"/>
    <stop offset="100%" stop-color="#E0A526" stop-opacity="0"/>
  </radialGradient>
</defs>
<rect width="1080" height="1350" fill="#0B0D10"/>
<rect width="1080" height="1350" fill="url(#g)"/>
<g font-family="IBM Plex Mono, monospace" fill="#CC252F" font-size="24" letter-spacing="4">
  <text x="80" y="120">BNI  |  ${t.eyebrow.toUpperCase()}</text>
</g>
<g font-family="Fraunces, serif" fill="#F7F4EE" font-size="140" letter-spacing="-3">
  <text x="80" y="360">${escapeXml(t.titleLine1)}</text>
</g>
<g font-family="Fraunces, serif" font-size="140" letter-spacing="-3">
  <text x="80" y="530" fill="#E23E47" font-style="italic">${escapeXml(t.titleAccent)}</text>
  <text x="80" y="700" fill="#F7F4EE">${escapeXml(t.titleRest)}</text>
</g>
<g font-family="Inter, sans-serif" fill="#C2BCB3" font-size="30">
  <text x="80" y="810">${escapeXml(t.vDate)}</text>
  <text x="80" y="860">${escapeXml(t.vVenue)}</text>
</g>
<g font-family="IBM Plex Mono, monospace" fill="#8B8378" font-size="22" letter-spacing="3">
  <text x="80" y="1250">${escapeXml(shareUrl.toUpperCase())}</text>
</g>
<rect x="80" y="1000" width="120" height="4" fill="#CC252F"/>
<text x="80" y="1075" font-family="Fraunces, serif" fill="#F7F4EE" font-size="50">Reserve your seat</text>
</svg>`;
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "bni-members-day-2026.svg";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    toast.success("Share card downloaded");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            className="w-full max-w-[420px]"
            onClick={(e) => e.stopPropagation()}
            data-testid="share-card-modal"
          >
            <div className="hairline p-5" style={{ background: "var(--panel)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="mono-label">Share the invite</div>
                  <div className="font-serif text-xl mt-1">Send-later card</div>
                </div>
                <button data-testid="share-close-btn" onClick={onClose}><X size={18} /></button>
              </div>

              <div className="mt-4 share-card" data-testid="share-card-preview">
                <div className="share-card-logo-wrap">
                  <img src="/risse-bni-combined.png" alt="RISSE BNI 26 — Davangere Members Day" className="share-card-logo-combined" data-testid="share-card-logo" />
                </div>
                <div>
                  <div className="mono-label" style={{ color: "var(--red)" }}>{t.eyebrow}</div>
                  <div className="sc-title">
                    {t.titleLine1}<br /><span className="kw">{t.titleAccent}</span> {t.titleRest}
                  </div>
                </div>
                <div>
                  <div className="text-sm" style={{ color: "var(--body)" }}>{t.vDate}</div>
                  <div className="text-sm mt-1" style={{ color: "var(--body)" }}>{t.vVenue}</div>
                  <div className="mono-label mt-4" style={{ color: "var(--taupe)" }} data-testid="share-card-url">https://risse.bnidavangere.com/</div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2">
                <button data-testid="share-copy-btn" onClick={copyCaption} className="btn-ghost !py-2 !px-2 !text-xs">
                  <Copy size={12} /> Caption
                </button>
                <a data-testid="share-wa-btn" href={waHref} target="_blank" rel="noreferrer" className="btn-ghost !py-2 !px-2 !text-xs">
                  <MessageCircle size={12} /> WhatsApp
                </a>
                <button data-testid="share-download-btn" onClick={downloadSvg} className="btn-red !py-2 !px-2 !text-xs">
                  <Download size={12} /> Card
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function escapeXml(s) {
  return String(s).replace(/[<>&'"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" }[c]));
}

// -------------- Countdown --------------
function Countdown({ target, t, compact = false }) {
  const tgt = React.useMemo(() => new Date(target).getTime(), [target]);
  const compute = () => {
    const diff = Math.max(0, tgt - Date.now());
    const days = Math.floor(diff / 86400000);
    const hrs  = Math.floor((diff % 86400000) / 3600000);
    const min  = Math.floor((diff % 3600000) / 60000);
    const sec  = Math.floor((diff % 60000) / 1000);
    return { days, hrs, min, sec };
  };
  const [now, setNow] = React.useState(compute);
  React.useEffect(() => {
    const id = setInterval(() => setNow(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tgt]);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className={`countdown ${compact ? "countdown-compact" : ""}`} data-testid="hero-countdown">
      <div className="count-cell"><div className="n">{now.days}</div><div className="u">{t?.cdDays || "Days"}</div></div>
      <div className="count-cell"><div className="n">{pad(now.hrs)}</div><div className="u">{t?.cdHrs || "Hrs"}</div></div>
      <div className="count-cell"><div className="n">{pad(now.min)}</div><div className="u">{t?.cdMin || "Min"}</div></div>
      <div className="count-cell"><div className="n">{pad(now.sec)}</div><div className="u">{t?.cdSec || "Sec"}</div></div>
    </div>
  );
}

// -------------- Agenda rail with progress --------------
function AgendaRail() {
  const wrapRef = useRef(null);
  const itemRefs = useRef([]);
  const [fillPx, setFillPx] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const wrap = wrapRef.current; if (!wrap) return;
      const wrapRect = wrap.getBoundingClientRect();
      const viewPivot = window.innerHeight * 0.42;
      // Determine active item = the LAST item whose dot has passed the viewPivot
      let idx = 0;
      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const dotY = r.top + 30; // ::before offset from item top
        if (dotY <= viewPivot) idx = i;
      });
      setActiveIdx(idx);
      // Compute fill height from top of wrap to active item's dot
      const el = itemRefs.current[idx];
      if (el) {
        const r = el.getBoundingClientRect();
        const dotY = r.top + 30;
        setFillPx(Math.max(0, Math.min(wrap.clientHeight, dotY - wrapRect.top)));
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);

  return (
    <div ref={wrapRef} className="agenda-wrap">
      <div className="agenda-rail" />
      <div className="agenda-rail-fill" style={{ height: `${fillPx}px` }} />
      {AGENDA.map((a, i) => (
        <div
          key={i}
          ref={(el) => itemRefs.current[i] = el}
          className={`agenda-item ${i === activeIdx ? "active" : ""} ${i < activeIdx ? "passed" : ""}`}
          data-testid={`agenda-item-${i}`}
        >
          <div className="time">{a.time}</div>
          <div>
            <h3>{a.h}</h3>
            <p>{a.p}</p>
            {a.tag && <span className="tag">{a.tag}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// -------------- FAQ item --------------
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? "open" : ""}`}>
      <div className="faq-q" onClick={() => setOpen(!open)} data-testid={`faq-q-${q.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}>
        {q}
        <span className="faq-plus">+</span>
      </div>
      <div className="faq-a"><p>{a}</p></div>
    </div>
  );
}

// -------------- Sticky bottom CTA --------------
function StickyCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const past = window.scrollY > 320;
      const el = document.getElementById("register-anchor");
      // Hide only while the register section still covers the bottom strip of the screen;
      // the bar slides back up the moment you scroll past it.
      const overlapping = el
        ? (() => { const r = el.getBoundingClientRect(); return r.top < window.innerHeight - 40 && r.bottom > window.innerHeight - 96; })()
        : false;
      setVisible(past && !overlapping);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);
  return (
    <div className={`sticky-cta ${visible ? "visible" : ""}`} data-testid="sticky-cta" aria-hidden={!visible}>
      <span className="label">Members Day · 28 Nov 2026</span>
      <a href="#register-anchor" className="sticky-cta-pill" data-testid="sticky-cta-btn">
        <span className="sticky-cta-glow" aria-hidden="true" />
        <UserPlus size={14} />
        <span className="sticky-cta-label">Register</span>
      </a>
    </div>
  );
}

// -------------- Success card (kept from earlier build) --------------
// -------------- Login modal (3-step: phone → OTP → role picker) --------------
const LOGIN_ROLES = [
  { id: "member",   route: "/member",   name: "Member Portal",    tag: "For attendees",             icon: "User" },
  { id: "leader",   route: "/leader",   name: "Table Leader App", tag: "Log referrals at your table", icon: "Users" },
  { id: "scanner",  route: "/scanner",  name: "Scanner App",      tag: "Meal & pass check-in",       icon: "Camera" },
  { id: "admin",    route: "/admin",    name: "Admin Dashboard",  tag: "Command centre",             icon: "LayoutDashboard" },
  { id: "devadmin", route: "/devadmin", name: "DevAdmin Console", tag: "Integrations & config",      icon: "Settings" },
];

